import { useEffect, useState } from "react";
import { Exercise } from "../../libs/exercises/types";
import { useGetSpecialCategoriesQuery } from "../../libs/specialCategory/api";
import { useCreateExerciseMutation, useUpdateExerciseMutation } from "../../libs/exercises/api";
import { DeleteButton, EditButton, PlusButton } from "../Button";
import { SubmitButton } from "../Button";
import { Modal } from "../Modal";
import { SuccessAlert } from "../Alerts";
import { DeleteExerciseModal } from "./DeleteExerciseModal";
import { Pagination } from "../Pagination";
import { useForm } from "react-hook-form";
import { searchExercise } from "../../libs/exercises/utils";
import { CHECK_DISTRICTS } from "../WorkoutTables/muscleConfig";

export const CHECK_NOTES_TEMPLATE = `COME COMPILARE
Inserisci il tuo voto nel campo REPS di ogni set.

SCALA
1 = Male / Molto
2 = Medio
3 = Bene / Poco o niente

DOMANDE
Set 1: Quanto dolore muscolare senti sul muscolo target?
Set 2: Quanto ti senti recuperato su questo muscolo?
Set 3: Qual e' il tuo livello di energia mentale e voglia di allenarti?
Set 4: Livello di stress e stanchezza fisica al di fuori dell'allenamento?

PUNTEGGIO (somma set 1+2 e set 3+4)
🟢 5-6 → TUTTO OK, SPINGI
🟡 3-4 → ATTENZIONE, MANTIENI
🔴 2 → CRITICO, RIDUCI E SENTI IL TUO COACH`;

export const CHECK_DEFAULT_SETS = [
  { reps: 1, repsRange: 3, rest: 0 },
  { reps: 1, repsRange: 3, rest: 0 },
  { reps: 1, repsRange: 3, rest: 0 },
  { reps: 1, repsRange: 3, rest: 0 },
];

type Props = {
  exercises?: Exercise[];
};

export const CheckExercisesSection: React.FC<Props> = ({ exercises }) => {
  const { data: categories } = useGetSpecialCategoriesQuery();
  const checkCategory = categories?.find((c) =>
    c.name.toUpperCase().includes("CHECK")
  );

  const checkExercises = exercises?.filter(
    (ex) => ex.specialCategoryId != null && checkCategory && ex.specialCategoryId === checkCategory.id
  );

  const [openModal, setOpenModal] = useState<string | undefined>();
  const [exerciseToUpdate, setExerciseToUpdate] = useState<Exercise | undefined>();
  const [filteredResults, setFilteredResults] = useState(checkExercises);
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  useEffect(() => {
    setSearchInput("");
    setFilteredResults(checkExercises);
  }, [exercises, checkCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredResults]);

  const searchItems = (searchValue: string) => {
    searchExercise(
      checkExercises || [],
      searchValue,
      setSearchInput,
      setFilteredResults
    );
  };

  if (!checkCategory) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400 text-sm mb-2">
          Nessuna categoria "CHECK" trovata.
        </p>
        <p className="text-gray-500 text-xs">
          Vai al tab "Categorie Speciali" e crea una categoria con nome contenente "CHECK".
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => searchItems(e.target.value)}
              className="block w-full pl-10 p-2.5 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
              placeholder="Cerca check..."
            />
          </div>
        </div>
        <button
          onClick={() => setOpenModal("create-check")}
          className="ml-4 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          + Nuovo Check
        </button>
      </div>

      {/* Table */}
      <div className="relative overflow-x-auto shadow-md dark:shadow-gray-900 sm:rounded-lg mb-6">
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3 text-center">Nome</th>
              <th scope="col" className="px-6 py-3 text-center">Distretto</th>
              <th scope="col" className="px-6 py-3 text-center">Tags</th>
              <th scope="col" className="px-6 py-3 text-center">Azione</th>
            </tr>
          </thead>
          <tbody>
            {filteredResults && filteredResults.length > 0 ? (
              filteredResults
                .slice((currentPage - 1) * perPage, currentPage * perPage)
                .map((exercise, i) => (
                  <tr
                    key={exercise.id}
                    className={`${i % 2 !== 0 ? "bg-gray-50 dark:bg-gray-700" : "bg-white dark:bg-gray-900"} dark:border-gray-700`}
                  >
                    <th scope="row" className="px-6 py-4 text-center font-medium text-gray-900 whitespace-nowrap dark:text-white">
                      {exercise.name}
                    </th>
                    <td className="px-6 py-4 text-center">
                      {exercise.checkDistrict ? (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                          {exercise.checkDistrict}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {exercise.tags?.map((tag) => (
                        <span key={tag} className="px-2 m-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                          {tag}
                        </span>
                      ))}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <EditButton
                        onClick={() => {
                          setExerciseToUpdate(exercise);
                          setOpenModal("edit-exercise");
                        }}
                        variant="icon"
                      />
                      <DeleteButton
                        onClick={() => {
                          setExerciseToUpdate(exercise);
                          setOpenModal("delete-exercise");
                        }}
                        variant="icon"
                      />
                    </td>
                  </tr>
                ))
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  Nessun check exercise trovato.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filteredResults && filteredResults.length > perPage && (
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil(filteredResults.length / perPage)}
        />
      )}

      {openModal === "create-check" && (
        <CreateCheckModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          checkCategoryId={checkCategory.id}
        />
      )}
      {openModal === "edit-exercise" && exerciseToUpdate && (
        <EditCheckModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          exercise={exerciseToUpdate}
        />
      )}
      {openModal === "delete-exercise" && (
        <DeleteExerciseModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          exerciseToDelete={exerciseToUpdate}
        />
      )}

      <div className="mb-20" />
    </>
  );
};

// ── Modal semplificata per creare Check ──────────────────────────────────────

type CreateCheckModalProps = {
  openModal: string | undefined;
  setOpenModal: (value: string | undefined) => void;
  checkCategoryId: number;
};

type CheckFormValues = {
  name: string;
  checkDistrict: string;
  tags: string[];
  notes: string;
};

const CreateCheckModal: React.FC<CreateCheckModalProps> = ({
  openModal,
  setOpenModal,
  checkCategoryId,
}) => {
  const [createExercise, { data: exercise, isLoading, error }] =
    useCreateExerciseMutation();
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<CheckFormValues>();

  const [tags, setTags] = useState<string[]>([]);
  const {
    register: registerTag,
    handleSubmit: handleTagSubmit,
    setValue: setTagValue,
  } = useForm<{ tag: string }>();

  const onSubmit = (data: CheckFormValues) => {
    createExercise({
      name: data.name,
      tags,
      videoUrls: [""],
      type: "DEFAULT",
      specialCategoryId: checkCategoryId,
      checkDistrict: data.checkDistrict || undefined,
      notes: data.notes || CHECK_NOTES_TEMPLATE,
    }).catch((err) => console.log(err));
  };

  const onTagSubmit = (data: { tag: string }) => {
    setTags([...tags, data.tag.toUpperCase()]);
    setTagValue("tag", "");
  };

  return (
    <Modal
      title="Crea nuovo Check Exercise"
      openModal={openModal}
      setOpenModal={setOpenModal}
    >
      <form className="space-y-5 p-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-3 text-xs text-blue-300">
          La categoria "CHECK" verrà assegnata automaticamente.
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Nome Check
          </label>
          <input
            {...register("name", { required: "Inserisci un nome" })}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            placeholder="es. Recovery Check Quad"
            required
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Distretto Muscolare
          </label>
          <select
            {...register("checkDistrict", { required: "Seleziona un distretto" })}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="">-- Seleziona distretto --</option>
            {CHECK_DISTRICTS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          {errors.checkDistrict && (
            <p className="mt-1 text-sm text-red-500">{errors.checkDistrict.message}</p>
          )}
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Messaggio default
          </label>
          <textarea
            {...register("notes")}
            rows={5}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            placeholder={CHECK_NOTES_TEMPLATE}
          />
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            Questo testo sarà pre-compilato automaticamente ogni volta che aggiungi il check a una settimana. Se lasci vuoto verrà usato il template di default.
          </p>
        </div>

        {/* Tags */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Tags
          </label>
          <div className="flex gap-2">
            <input
              {...registerTag("tag", { required: true })}
              className="flex-1 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Aggiungi un tag..."
            />
            <PlusButton variant="icon" onClick={handleTagSubmit(onTagSubmit)} />
          </div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded font-medium dark:text-gray-300"
                >
                  {tag}
                  <button type="button" onClick={() => setTags(tags.filter((t) => t !== tag))}>
                    <svg className="w-3 h-3 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {!exercise && (
          <div className="w-full">
            <SubmitButton isPending={isLoading}>Crea Check Exercise</SubmitButton>
          </div>
        )}
      </form>

      {exercise && (
        <SuccessAlert>
          <span className="font-medium">Check creato con successo!</span>{" "}
          Ora puoi aggiungerlo nelle schede workout.
        </SuccessAlert>
      )}
      {error && (
        <p className="p-4 text-sm font-medium text-red-500">
          Errore nella creazione. Riprova.
        </p>
      )}
    </Modal>
  );
};

// ── Modal semplificata per modificare Check ──────────────────────────────────

type EditCheckModalProps = {
  openModal: string | undefined;
  setOpenModal: (value: string | undefined) => void;
  exercise: Exercise;
};

const EditCheckModal: React.FC<EditCheckModalProps> = ({
  openModal,
  setOpenModal,
  exercise,
}) => {
  const [updateExercise, { data: updated, isLoading, error }] =
    useUpdateExerciseMutation();
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<{ name: string; checkDistrict: string; notes: string }>({
    defaultValues: { name: exercise.name, checkDistrict: exercise.checkDistrict || "", notes: exercise.notes || "" },
  });

  const [tags, setTags] = useState<string[]>(exercise.tags || []);
  const {
    register: registerTag,
    handleSubmit: handleTagSubmit,
    setValue: setTagValue,
  } = useForm<{ tag: string }>();

  const onSubmit = (data: { name: string; checkDistrict: string; notes: string }) => {
    updateExercise({
      id: exercise.id,
      name: data.name,
      tags,
      videoUrls: exercise.videoUrls || [""],
      type: exercise.type,
      specialCategoryId: exercise.specialCategoryId,
      checkDistrict: data.checkDistrict || undefined,
      notes: data.notes || undefined,
    }).unwrap().then(() => setOpenModal(undefined)).catch((err) => console.log(err));
  };

  const onTagSubmit = (data: { tag: string }) => {
    setTags([...tags, data.tag.toUpperCase()]);
    setTagValue("tag", "");
  };

  return (
    <Modal
      title="Modifica Check Exercise"
      openModal={openModal}
      setOpenModal={setOpenModal}
    >
      <form className="space-y-5 p-4" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Nome Check
          </label>
          <input
            {...register("name", { required: "Inserisci un nome" })}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            required
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Distretto Muscolare
          </label>
          <select
            {...register("checkDistrict", { required: "Seleziona un distretto" })}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="">-- Seleziona distretto --</option>
            {CHECK_DISTRICTS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Messaggio default
          </label>
          <textarea
            {...register("notes")}
            rows={5}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            placeholder={CHECK_NOTES_TEMPLATE}
          />
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            Testo pre-compilato automaticamente quando aggiungi il check a una settimana.
          </p>
        </div>

        {/* Tags */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Tags
          </label>
          <div className="flex gap-2">
            <input
              {...registerTag("tag", { required: true })}
              className="flex-1 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Aggiungi un tag..."
            />
            <PlusButton variant="icon" onClick={handleTagSubmit(onTagSubmit)} />
          </div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded font-medium dark:text-gray-300"
                >
                  {tag}
                  <button type="button" onClick={() => setTags(tags.filter((t) => t !== tag))}>
                    <svg className="w-3 h-3 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {!updated && (
          <div className="w-full">
            <SubmitButton isPending={isLoading}>Salva modifiche</SubmitButton>
          </div>
        )}
      </form>

      {updated && (
        <SuccessAlert>
          <span className="font-medium">Modifica avvenuta con successo!</span>
        </SuccessAlert>
      )}
      {error && (
        <p className="p-4 text-sm font-medium text-red-500">
          Errore nella modifica. Riprova.
        </p>
      )}
    </Modal>
  );
};
